import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fail } from 'features/toast/Toast'
import { Link, useHistory, useRouteMatch } from 'react-router-dom'
import {
  deleteSelectedCreatives,
  fetchActiveCreatives,
  fetchCreatives,
  setPage,
  setSelectedIds,
  setSize,
  setSort,
} from './creativeListSlice'
import PageHeader from '../../../components/PageHeader'
import Button from '../../../components/form/Button'
import { useConfirm } from '../../dialog/Confirm'
import CreativeListFilter from './CreativeListFilter'
import Table from '../../../components/table/Table'
import classNames from 'classnames'
import CreativeListItem from './CreativeListItem'
import Pagination from '../../../components/pagination/Pagination'
import PageSize from '../../../components/pagination/PageSize'
import ReviewReasonModal from '../../reviewReason/ReviewReasonModal'

const DEFAULT_DIRECTIONS = {
  name: 'asc',
  reviewStatus: 'asc',
  createdDate: 'desc',
}

const CreativeList = () => {
  const dispatch = useDispatch()
  const {
    isCreativesLoading,
    page,
    total,
    error,
    size,
    creatives,
    selectedIds,
    activeCreativeSize,
    sort,
    direction,
  } = useSelector(state => state.creative.creativeList)
  const {
    url,
    params: { accountId },
  } = useRouteMatch()
  const history = useHistory()
  const [DeleteCreativeConfirm, showDeleteCreativeConfirm] = useConfirm()

  useEffect(() => {
    dispatch(fetchCreatives())
  }, [page, sort, direction, size, dispatch])
  useEffect(() => {
    dispatch(fetchActiveCreatives())
  }, [creatives, dispatch])
  useEffect(
    () => () => {
      dispatch(setSelectedIds([]))
    },
    [dispatch]
  )

  const handleSelectionChange = useCallback(
    ids => dispatch(setSelectedIds(ids)),
    [dispatch]
  )
  const handleConfirmDelete = useCallback(
    async ({ isConfirmed }) => {
      if (isConfirmed) {
        const { error: deleteError } = await dispatch(deleteSelectedCreatives())
        if (deleteError) {
          fail(deleteError.message)
        } else {
          dispatch(fetchCreatives())
        }
      }
    },
    [dispatch]
  )
  const handlePageChange = useCallback(
    newPage => dispatch(setPage(newPage)),
    [dispatch]
  )
  const handleSizeChange = useCallback(v => dispatch(setSize(v)), [dispatch])
  const handleEditAddClick = useCallback(() => {
    if (selectedIds.length > 1) {
      fail('하나의 소재만 선택해주세요.')
    } else {
      history.push(`/${accountId}/creatives/add/${selectedIds[0]}`)
    }
  }, [accountId, history, selectedIds])
  const handleSort = useCallback(
    value => () => {
      dispatch(
        setSort({
          sort: value,
          direction:
            value === sort
              ? direction === 'desc'
                ? 'asc'
                : 'desc'
              : DEFAULT_DIRECTIONS[value],
        })
      )
    },
    [dispatch, sort, direction]
  )

  return (
    <>
      <div className="meterialmanage_wrap">
        <div className="dsp_tit">
          <PageHeader pageTitle="광고소재 관리" />
        </div>
        <div className="ad_managebox">
          <div className="managebox_tit">
            <strong className="tit_box">
              총 소재<span className="fc_emph"> {activeCreativeSize}</span>
            </strong>
            <div className="f_left">
              <div className="single_wrap">
                <Link to={`${url}/add`} className="btn_gm gm_bl">
                  <span className="inner_g">
                    <span className="img_comm ico_add" />
                    소재 등록하기
                  </span>
                </Link>
              </div>
              <div className="single_wrap">
                <Button
                  line
                  disabled={selectedIds.length === 0}
                  onClick={showDeleteCreativeConfirm}>
                  삭제
                </Button>
              </div>
              <div className="single_wrap">
                <Button
                  line
                  disabled={selectedIds.length === 0}
                  onClick={handleEditAddClick}>
                  편집하여 새로등록
                </Button>
              </div>
            </div>
            <div className="f_right">
              <CreativeListFilter />
            </div>
          </div>
          <div className="tblg2_wrap">
            <Table
              className="tbl_g2"
              caption="광고소재 관리 표"
              emptyMessage={
                <>
                  등록된 광고소재가 없습니다.
                  <br />
                  <Link to={`${url}/add`} className="link_txt">
                    소재 등록하기
                  </Link>
                  를 선택하여 새로운 소재를 등록하세요.
                </>
              }
              error={error !== null}
              loading={isCreativesLoading}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}>
              <Table.Head>
                <Table.Column width={45} checkbox />
                <Table.Column
                  title={
                    <>
                      소재 이름<span className="ico_comm ico_sort">정렬</span>
                    </>
                  }
                  onClick={handleSort('name')}
                  thClassName={classNames('sortable', {
                    sort_on: sort === 'name',
                    sort_down: sort === 'name' && direction === 'desc',
                    sort_up:
                      sort !== 'name' ||
                      (sort === 'name' && direction === 'asc'),
                  })}
                  width={184}
                />
                <Table.Column
                  title={
                    <>
                      심사 상태<span className="ico_comm ico_sort">정렬</span>
                    </>
                  }
                  onClick={handleSort('reviewStatus')}
                  thClassName={classNames('sortable', {
                    sort_on: sort === 'reviewStatus',
                    sort_down: sort === 'reviewStatus' && direction === 'desc',
                    sort_up:
                      sort !== 'reviewStatus' ||
                      (sort === 'reviewStatus' && direction === 'asc'),
                  })}
                  width={97}
                />
                <Table.Column title="소재 정보" />
                <Table.Column title="비즈채널 유형" width={108} />
                <Table.Column title="비즈채널" width={158} />
                <Table.Column title="비즈채널 운영" width={109} />
                <Table.Column title="그룹" width={49} />
                <Table.Column
                  title={
                    <>
                      등록일<span className="ico_comm ico_sort">정렬</span>
                    </>
                  }
                  onClick={handleSort('createdDate')}
                  thClassName={classNames('sortable', {
                    sort_on: sort === 'createdDate',
                    sort_down:
                      sort !== 'createdDate' ||
                      (sort === 'createdDate' && direction === 'desc'),
                    sort_up: sort === 'createdDate' && direction === 'asc',
                  })}
                  width={99}
                />
              </Table.Head>
              <Table.Body>
                {creatives.map(creative => (
                  <CreativeListItem key={creative.id} creative={creative} />
                ))}
              </Table.Body>
            </Table>
          </div>
          {total > 0 && (
            <div className="paging_wrap">
              <div className="inner_paging">
                <Pagination
                  total={total}
                  size={size}
                  current={page}
                  onChange={handlePageChange}
                />
                <PageSize
                  onSizeChange={v => {
                    handlePageChange(0)
                    handleSizeChange(v)
                  }}
                  size={size}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <ReviewReasonModal />
      <DeleteCreativeConfirm title="소재 삭제" onClose={handleConfirmDelete}>
        해당 소재를 삭제하시겠습니까?
        <br />
        삭제시 등록된 광고그룹에서도 모두 삭제됩니다.
      </DeleteCreativeConfirm>
    </>
  )
}

export default CreativeList
